import { useEffect, useId, useState } from 'react';

import { Button, Divider } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { completarEndereco, consultarCep, digitosDoCep } from '@/comuns/cep';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { NovoUsuarioDaEquipe } from '@/data/tipos';

const PAPEIS: { value: NovoUsuarioDaEquipe['papel']; label: string }[] = [
    { value: 'profissional', label: 'Profissional' },
    { value: 'recepcao', label: 'Recepção' },
    { value: 'financeiro', label: 'Financeiro' },
];

export const SENHA_MINIMA = 8;

/** Sem 0/O e 1/l/I: a senha é ditada ou copiada à mão para a pessoa. */
const ALFABETO = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function gerarSenha(tamanho = 12): string {
    const bytes = new Uint32Array(tamanho);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => ALFABETO[b % ALFABETO.length]).join('');
}

/** As partes do endereço (0163), as mesmas da edição e na mesma ordem. */
const ENDERECO = ['cep', 'estado', 'cidade', 'bairro', 'rua', 'numero', 'complemento'] as const;

const VAZIO = {
    nome: '', email: '', senha: '', papel: 'profissional' as NovoUsuarioDaEquipe['papel'],
    telefone: '', especialidades: '', registro: '',
    cep: '', estado: '', cidade: '', bairro: '', rua: '', numero: '', complemento: '',
};

export interface NovoUsuarioDaEquipeModalProps {
    open: boolean;
    onClose: () => void;
    onCriado: () => void;
}

/**
 * Cria a pessoa da equipe já com login e senha — sem convite por e-mail.
 *
 * A senha aparece na tela até o modal fechar: é quem administra que a entrega à
 * pessoa, e ela não volta a ser mostrada depois.
 */
export function NovoUsuarioDaEquipeModal({ open, onClose, onCriado }: NovoUsuarioDaEquipeModalProps) {
    const aviso = useAviso();
    const ids = {
        nome: useId(), email: useId(), senha: useId(), papel: useId(), telefone: useId(), funcao: useId(), registro: useId(),
        cep: useId(), estado: useId(), cidade: useId(), bairro: useId(), rua: useId(), numero: useId(), complemento: useId(),
    };

    const [form, setForm] = useState(VAZIO);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(VAZIO);
        setErro(null);
    }, [open]);

    const mudar = (m: Partial<typeof VAZIO>) => { setForm((f) => ({ ...f, ...m })); setErro(null); };
    const sujo = JSON.stringify(form) !== JSON.stringify(VAZIO);
    const ehProfissional = form.papel === 'profissional';

    const criar = async (): Promise<boolean> => {
        if (!form.nome.trim()) { setErro('Informe o nome da pessoa.'); return false; }
        if (!form.email.trim()) { setErro('Informe o e-mail: é com ele que a pessoa entra.'); return false; }
        if (form.senha.length < SENHA_MINIMA) { setErro(`A senha precisa de pelo menos ${SENHA_MINIMA} caracteres.`); return false; }
        /* Parte vazia do endereço não vai: o cadastro nasce com a coluna nula,
           em vez de com um texto em branco que a lista mostraria como espaço. */
        const endereco: Partial<NovoUsuarioDaEquipe> = {};
        for (const parte of ENDERECO) {
            const valor = form[parte].trim();
            if (valor) endereco[parte] = valor;
        }

        setSalvando(true);
        try {
            const criado = await dados().criarUsuarioDaEquipe({
                nome: form.nome.trim(),
                email: form.email.trim(),
                senha: form.senha,
                papel: form.papel,
                ...(form.telefone.trim() ? { telefone: form.telefone.trim() } : {}),
                ...(ehProfissional && form.especialidades.trim() ? { especialidades: form.especialidades.trim() } : {}),
                ...(ehProfissional && form.registro.trim() ? { registro: form.registro.trim() } : {}),
                ...endereco,
            });
            aviso({ title: 'Usuário criado', description: `${form.nome.trim()} já pode entrar com ${criado.email}.` });
            onCriado();
            onClose();
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível criar o usuário.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={open} onClose={onClose} width={520} title="Novo usuário da equipe"
            description="A conta nasce ativa, com a senha que você definir. Entregue a senha à pessoa: nenhum e-mail é enviado."
            dirty={sujo} onSave={criar} saveLabel="Criar usuário"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void criar()} loading={salvando}>Criar usuário</Button>
                </>
            }
        >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {erro && (
                    <div style={{ gridColumn: '1 / -1' }}>
                        <Alert tone="danger" dismissible={false}>{erro}</Alert>
                    </div>
                )}
                <FormField label="Nome completo" required htmlFor={ids.nome} style={{ gridColumn: '1 / -1' }}>
                    <Input id={ids.nome} value={form.nome} onChange={(v) => mudar({ nome: v })} placeholder="Nome da pessoa" />
                </FormField>
                <FormField label="E-mail de acesso" required htmlFor={ids.email} style={{ gridColumn: '1 / -1' }}>
                    <Input id={ids.email} type="email" value={form.email} onChange={(v) => mudar({ email: v })} placeholder="nome@email.com" />
                </FormField>
                <FormField
                    label="Senha" required htmlFor={ids.senha} style={{ gridColumn: '1 / -1' }}
                    hint={`Pelo menos ${SENHA_MINIMA} caracteres. A pessoa pode trocar depois, pelo "esqueci minha senha".`}
                >
                    <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <Input id={ids.senha} value={form.senha} onChange={(v) => mudar({ senha: v })} placeholder="Defina a senha" autoComplete="new-password" />
                        </div>
                        <Button variant="secondary" onClick={() => mudar({ senha: gerarSenha() })}>Gerar</Button>
                    </div>
                </FormField>
                <FormField label="Papel" required htmlFor={ids.papel}>
                    <Select id={ids.papel} value={form.papel} onChange={(v) => mudar({ papel: v as NovoUsuarioDaEquipe['papel'] })} options={PAPEIS} />
                </FormField>
                <FormField label="Telefone" htmlFor={ids.telefone}>
                    <Input id={ids.telefone} type="tel" value={form.telefone} onChange={(v) => mudar({ telefone: v })} placeholder="(11) 99999-9999" />
                </FormField>
                {ehProfissional && (
                    <>
                        <FormField label="Função" htmlFor={ids.funcao}>
                            <Input id={ids.funcao} value={form.especialidades} onChange={(v) => mudar({ especialidades: v })} placeholder="Dermatologista" />
                        </FormField>
                        <FormField label="Registro no conselho" htmlFor={ids.registro}>
                            <Input id={ids.registro} value={form.registro} onChange={(v) => mudar({ registro: v })} placeholder="CRM-SP 000.000" />
                        </FormField>
                    </>
                )}
                {/* Endereço (0163): opcional na criação, igual à edição. Quem
                    cadastra às pressas deixa em branco e completa depois. */}
                <Divider label="Endereço" spacing={4} style={{ gridColumn: '1 / -1' }} />
                <FormField label="CEP" htmlFor={ids.cep} hint="Ao sair do campo, o endereço vazio é completado pelo CEP.">
                    <Input
                        id={ids.cep} value={form.cep} onChange={(v) => mudar({ cep: v })} placeholder="00000-000"
                        onBlur={() => {
                            if (!digitosDoCep(form.cep)) return;
                            void consultarCep(form.cep).then((r) => {
                                /* `completarEndereco` só preenche o que está vazio:
                                   o que a pessoa digitou continua dela. */
                                if (r.ok) setForm((f) => completarEndereco(f, r.endereco));
                                else aviso({ title: 'Endereço não completado', description: r.motivo });
                            });
                        }}
                    />
                </FormField>
                <FormField label="Estado" htmlFor={ids.estado}>
                    <Input id={ids.estado} value={form.estado} onChange={(v) => mudar({ estado: v })} placeholder="SP" />
                </FormField>
                <FormField label="Cidade" htmlFor={ids.cidade}>
                    <Input id={ids.cidade} value={form.cidade} onChange={(v) => mudar({ cidade: v })} />
                </FormField>
                <FormField label="Bairro" htmlFor={ids.bairro}>
                    <Input id={ids.bairro} value={form.bairro} onChange={(v) => mudar({ bairro: v })} />
                </FormField>
                <FormField label="Rua" htmlFor={ids.rua}>
                    <Input id={ids.rua} value={form.rua} onChange={(v) => mudar({ rua: v })} />
                </FormField>
                <FormField label="Número" htmlFor={ids.numero}>
                    <Input id={ids.numero} value={form.numero} onChange={(v) => mudar({ numero: v })} />
                </FormField>
                <FormField label="Complemento" htmlFor={ids.complemento} style={{ gridColumn: '1 / -1' }}>
                    <Input id={ids.complemento} value={form.complemento} onChange={(v) => mudar({ complemento: v })} placeholder="Apartamento, bloco, sala" />
                </FormField>
            </div>
        </Modal>
    );
}
